import { cn } from "@/lib/cn";
import { X } from "lucide-react";
import { type KeyboardEvent, useState } from "react";

interface TagChipInputProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
}

export function TagChipInput({ tags, onChange, suggestions }: TagChipInputProps) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);

  const filteredSuggestions = suggestions.filter(
    (s) =>
      s.toLowerCase().includes(inputValue.toLowerCase()) &&
      !tags.includes(s) &&
      inputValue.length > 0,
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault();
      const newTag = inputValue.trim();
      if (!tags.includes(newTag)) {
        onChange([...tags, newTag]);
      }
      setInputValue("");
      setShowSuggestions(false);
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter((t) => t !== tagToRemove));
  };

  const addSuggestion = (suggestion: string) => {
    if (!tags.includes(suggestion)) {
      onChange([...tags, suggestion]);
    }
    setInputValue("");
    setShowSuggestions(false);
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-2 items-center">
        {tags.map((tag) => (
          <span
            key={tag}
            className={cn(
              "inline-flex items-center gap-1.5",
              "px-2 py-1 rounded",
              "border border-zinc-300 dark:border-zinc-700",
              "bg-zinc-50 dark:bg-zinc-900",
              "font-mono text-xs",
              "text-zinc-700 dark:text-zinc-300",
            )}
          >
            {tag}
            <button
              type="button"
              onClick={() => removeTag(tag)}
              aria-label={`remove ${tag}`}
              className="hover:text-zinc-900 dark:hover:text-zinc-100"
            >
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(e.target.value.length > 0);
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(inputValue.length > 0)}
          onBlur={() => {
            // Delay to allow click on suggestion
            setTimeout(() => setShowSuggestions(false), 200);
          }}
          placeholder="Add tag…"
          className={cn(
            "px-2 py-1 rounded",
            "border border-zinc-300 dark:border-zinc-700",
            "bg-white dark:bg-zinc-950",
            "text-sm",
            "focus:outline-none focus:ring-2 focus:ring-accent",
            "min-w-[120px]",
          )}
        />
      </div>

      {showSuggestions && filteredSuggestions.length > 0 && (
        <div
          className={cn(
            "absolute top-full left-0 mt-1 z-10",
            "w-full max-w-xs",
            "border border-zinc-300 dark:border-zinc-700",
            "bg-white dark:bg-zinc-950",
            "rounded shadow-lg",
            "max-h-48 overflow-y-auto",
          )}
        >
          {filteredSuggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => addSuggestion(suggestion)}
              className={cn(
                "w-full text-left px-3 py-2",
                "hover:bg-zinc-100 dark:hover:bg-zinc-800",
                "text-sm font-mono",
                "text-zinc-700 dark:text-zinc-300",
              )}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
